// Which layout the city page uses on this screen. The four layouts are free to diverge: a phone held upright gets a
// stripped-down screen (map, clock, one action, one menu), while tablet, computer and giant display keep the full
// interface for now. A Tesla's own browser gets "Tesla mode": the phone's simple screen laid out for a wide landscape
// touch screen read at arm's length. `?layout=phone|tablet|pc|giant|tesla` forces one for testing.
import { useSyncExternalStore } from 'react'

export type DeviceClass = 'phone-portrait' | 'phone-landscape' | 'tablet' | 'pc' | 'giant-display' | 'tesla'
export interface Screen { width: number; height: number; coarse: boolean }

/** Phones are under 600 css px on their short side; giant displays are 2200 px wide or more; touch-first between is a tablet. */
export function classify({ width, height, coarse }: Screen): DeviceClass {
  const short = Math.min(width, height), portrait = height >= width
  if (short < 600) return portrait ? 'phone-portrait' : coarse && height < 500 ? 'phone-landscape' : 'tablet'
  if (width >= 2200) return 'giant-display'
  if (coarse && short < 1100) return 'tablet'
  return 'pc'
}

const FORCE: Record<string, DeviceClass> = { phone: 'phone-portrait', 'phone-portrait': 'phone-portrait', 'phone-landscape': 'phone-landscape', tablet: 'tablet', pc: 'pc', giant: 'giant-display', 'giant-display': 'giant-display', tesla: 'tesla' }
export function forcedClass(search = globalThis.location?.search ?? ''): DeviceClass | null {
  return FORCE[new URLSearchParams(search).get('layout') ?? ''] ?? null
}

/**
 * A Tesla's in-car browser. It says so in its user agent ("Tesla/2026.21.300", older ones "QtCarBrowser"); its screen
 * size and pointer cannot tell it apart (since the 2026 summer update it reports a fine pointer and hover, at 1254x784).
 */
export function isTeslaUA(ua = globalThis.navigator?.userAgent ?? ''): boolean {
  return /\bTesla\/(?:(?:develop|feature|terminal-das-fsd-eap)-)?[0-9]|QtCarBrowser/i.test(ua)
}
/** How each layout is named where the page shows it. */
export const LAYOUT_LABEL: Record<DeviceClass, string> = { 'phone-portrait': 'Phone', 'phone-landscape': 'Phone', tablet: 'Tablet', pc: 'Computer', 'giant-display': 'Giant display', tesla: 'Tesla mode' }

let last: DeviceClass | null = null
function read(): DeviceClass {
  const forced = forcedClass(); if (forced) return forced
  const w = globalThis.window
  if (!w) return 'pc'
  if (isTeslaUA()) return 'tesla'
  const coarse = !!w.matchMedia?.('(pointer: coarse)').matches
  const next = classify({ width: w.innerWidth, height: w.innerHeight, coarse })
  if (next !== last) last = next
  return last
}
function subscribe(f: () => void) {
  const w = globalThis.window; if (!w) return () => {}
  w.addEventListener('resize', f); w.addEventListener('orientationchange', f)
  return () => { w.removeEventListener('resize', f); w.removeEventListener('orientationchange', f) }
}
export const useDeviceClass = (): DeviceClass => useSyncExternalStore(subscribe, read, () => 'pc')
/** The stripped-down phone layout (upright phone). */
export const usePhoneLayout = (): boolean => useDeviceClass() === 'phone-portrait'
/** The simple screen: an upright phone, or Tesla mode. Null on every screen that keeps the full interface. */
export const useSimpleLayout = (): 'phone-portrait' | 'tesla' | null => { const d = useDeviceClass(); return d === 'phone-portrait' || d === 'tesla' ? d : null }
